import { Camera as BJSCamera } from "@babylonjs/core/Cameras/camera";
import { TargetCamera } from "@babylonjs/core/Cameras/targetCamera";
import { Matrix, Quaternion, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { degToRad, EPSILON, eq } from "../../utils/math";
export class Camera extends TargetCamera {
    get view() {
        return (this.mView);
    }
    constructor(pView) {
        super("", Vector3.Zero(), pView.viewer.scene);
        this.mView = pView;
        this.mProjectionOffsetX = 0;
        this.mProjectionOffsetY = 0;
        this.mFixedAspectRatio = null;
        this.mProjectionChanged = true;
        this.fov = Camera._DEFAULT_FOV;
        //init ortho
        this.orthoTop = 1;
        this.orthoBottom = -this.orthoTop;
        this.orthoLeft = -this.orthoTop;
        this.orthoRight = this.orthoTop;
    }
    setTarget(pTarget) {
        this._initialFocalDistance = pTarget.subtract(this.position).length();
        //fix for camera looking exactly in x direction
        if (eq(this.position.z, pTarget.z, EPSILON_SMALL) && !eq(this.position.x, pTarget.x, EPSILON_SMALL)) {
            this.position.z += EPSILON;
        }
        this._referencePoint.normalize().scaleInPlace(this._initialFocalDistance);
        const tVDir = gTmpVector3_1;
        pTarget.subtractToRef(this.position, tVDir);
        //prevent numerical issues with FireFox
        if (eq(tVDir.x, 0.0, EPSILON_SMALL))
            tVDir.x = 0.0;
        if (eq(tVDir.z, 0.0, EPSILON_SMALL))
            tVDir.z = 0.0;
        //select correct up-vector
        let tUp = Camera.UP_Y;
        const tVDirNorm = gTmpVector3_2;
        tVDir.normalizeToRef(tVDirNorm);
        if (tVDirNorm.y >= 1)
            tUp = Camera.UP_NEG_Z;
        else if (tVDirNorm.y <= -1)
            tUp = Camera.UP_Z;
        this.upVector.copyFrom(tUp);
        Matrix.LookAtLHToRef(this.position, pTarget, tUp, this._camMatrix);
        this._camMatrix.invert();
        //prevent numerical issues with FireFox
        if (eq(this._camMatrix.m[6], 0.0, EPSILON_SMALL))
            this._camMatrix.m[6] = 0.0;
        if (eq(this._camMatrix.m[10], 0.0, EPSILON_SMALL))
            this._camMatrix.m[10] = 0.0;
        //x-axis
        this.rotation.x = Math.atan(this._camMatrix.m[6] / this._camMatrix.m[10]);
        if (isNaN(this.rotation.x)) {
            this.rotation.x = 0;
        }
        //y-axis
        if (tVDir.x >= 0.0) {
            this.rotation.y = (-Math.atan(tVDir.z / tVDir.x) + Math.PI / 2.0);
        }
        else {
            this.rotation.y = (-Math.atan(tVDir.z / tVDir.x) - Math.PI / 2.0);
        }
        if (isNaN(this.rotation.y)) {
            this.rotation.y = 0;
        }
        //z-axis
        this.rotation.z = 0;
        if (this.rotationQuaternion) {
            Quaternion.RotationYawPitchRollToRef(this.rotation.y, this.rotation.x, this.rotation.z, this.rotationQuaternion);
        }
    }
    getProjectionMatrix(pForce) {
        const tIsFrozen = this._doNotComputeProjectionMatrix; //access private member of BabylonJS Camera
        if (!tIsFrozen && (this.mProjectionChanged || pForce)) {
            this._refreshFrustumPlanes = true; //access private member of BabylonJS Camera
            this.mProjectionChanged = false;
            const tAspectRatio = this.mFixedAspectRatio != null ?
                this.mFixedAspectRatio : this.getEngine().getAspectRatio(this);
            //update cache, used from BabylonJS GLTF serializer
            this._cache.fov = this.fov;
            this._cache.fovMode = this.fovMode;
            this._cache.aspectRatio = tAspectRatio;
            if (this.mode === BJSCamera.PERSPECTIVE_CAMERA) {
                Matrix.PerspectiveFovLHToRef(this.fov, tAspectRatio, this.minZ, this.maxZ, this._projectionMatrix, this.fovMode === BJSCamera.FOVMODE_VERTICAL_FIXED);
            }
            else {
                Matrix.OrthoOffCenterLHToRef(this.orthoLeft, this.orthoRight, this.orthoBottom, this.orthoTop, this.minZ, this.maxZ, this._projectionMatrix);
            }
        }
        //apply projection offset
        if (this.mode === BJSCamera.PERSPECTIVE_CAMERA) {
            this._projectionMatrix.m[8] = this.mProjectionOffsetX;
            this._projectionMatrix.m[9] = this.mProjectionOffsetY;
        }
        else {
            this._projectionMatrix.m[12] = this.mProjectionOffsetX;
            this._projectionMatrix.m[13] = this.mProjectionOffsetY;
        }
        return (this._projectionMatrix);
    }
    notifyProjectionChanged() {
        this.mProjectionChanged = true;
    }
    /** @internal */
    _setProjectionOffset(pX, pY) {
        this.mProjectionOffsetX = pX;
        this.mProjectionOffsetY = pY;
        this._projectionMatrix.markAsUpdated();
    }
    /** @internal */
    _setFixedAspectRatio(pAspect) {
        this.mFixedAspectRatio = pAspect;
    }
}
//// internal //////////////////////////////////////////////////////////////////////////////
/** @internal */
Camera._DEFAULT_FOV = degToRad(45);
//// private ///////////////////////////////////////////////////////////////////////////////
Camera.UP_Y = new Vector3(0, 1, 0);
Camera.UP_Z = new Vector3(0, 0, 1);
Camera.UP_NEG_Z = new Vector3(0, 0, -1);
//// temp variables ////////////////////////////////////////////////////////////////////////////
const EPSILON_SMALL = 0.00000001;
const gTmpVector3_1 = Vector3.Zero();
const gTmpVector3_2 = Vector3.Zero();
